import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { usePlanActual } from "../lib/planes";

// ═══════════════════════════════════════════════════════════════════════════════
// LIM.5 (4 Oct 2026) -- el consumo de Sofi del mes, pedido UNA vez por clínica y compartido
// por el aviso de pausa de AppLayout y la tarjeta del Dashboard (dos peticiones serían dos
// respuestas que pueden discrepar en pantalla).
//
// 🔴 TODO lo calcula agency (`GET /api/consumo` -> `/internal/clinic/{id}/consumo`): límite,
//    usadas, porcentaje, estado, saldo, pausa, si el paquete se puede vender. Aquí no se suma,
//    no se divide y no se deduce ningún estado.
// 🔴 Un plan sin Sofi NO pregunta fuera de Planes. CANCEL.1 consulta allí con configuracion
//    para leer su suscripción Stripe. La demo pública nunca pregunta (backend: 403).
// 🔴 Un error NO se pinta: sin dato, no hay tarjeta ni aviso (no hay textos aprobados para eso).
//
// B9 («se sumaron N conversaciones») sólo se afirma con una señal real: antes de ir a Stripe se
// guarda el saldo de ese momento, y al volver con `?paquete=ok` (el `success_url` de agency) sólo
// se dice si el saldo SUBIÓ. Si el pago queda pendiente (SPEI) lo dice `pagos_pendientes` (B8). Si
// no llega ninguna de las dos señales en ~30 s, no se dice nada.
// ═══════════════════════════════════════════════════════════════════════════════

const ConsumoCtx = createContext({ consumo: null, recarga: null, iniciarRecarga: null, refrescarConsumo: null });

const CLAVE_RECARGA = "surit_lim5_recarga";
const SONDEOS_TRAS_PAGO = 8;
const INTERVALO_SONDEO_MS = 4000;

export function ConsumoProvider({ children }) {
  const { user, activeClinic, puede } = useAuth();
  const location = useLocation();
  const planActual = usePlanActual();
  const [searchParams, setSearchParams] = useSearchParams();
  const [consumo, setConsumo] = useState(null);
  const [recarga, setRecarga] = useState(null); // null | "acreditada"
  const [recargaConv, setRecargaConv] = useState(null);
  const vueltaDePago = useRef(null);

  const cid = activeClinic?.id || null;
  const esDemo = !!user?.is_demo_session;
  // Mientras el catálogo carga no se pregunta (un plan Sistema no debe sondear ni una vez). Una
  // clínica sin `plan_slug` deja `usePlanActual` en "cargando" para siempre: ahí sí se pregunta.
  const planDecidido = planActual.estado !== "cargando" || !activeClinic?.plan_slug;
  // CANCEL.1: en Planes también necesitamos la señal Stripe de los planes Sistema.
  const consultaPlan = location.pathname.replace(/\/$/, "") === "/app/onboarding" && puede?.("configuracion");
  const debePreguntar = !!cid && !esDemo && planDecidido && (!planActual.sinSofi || consultaPlan);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/consumo");
      setConsumo(data && typeof data === "object" ? data : null);
      return data;
    } catch {
      setConsumo(null);
      return null;
    }
  }, []);

  // La vuelta de Stripe: se lee y se quita de la URL una sola vez.
  useEffect(() => {
    const p = searchParams.get("paquete");
    if (!p) return;
    vueltaDePago.current = p;
    const resto = new URLSearchParams(searchParams);
    resto.delete("paquete");
    setSearchParams(resto, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setConsumo(null);
    setRecarga(null);
    if (!debePreguntar) return undefined;
    let vivo = true;
    let timer = null;
    (async () => {
      const data = await cargar();
      if (!vivo) return;
      if (vueltaDePago.current !== "ok") {
        if (vueltaDePago.current) { sessionStorage.removeItem(CLAVE_RECARGA); vueltaDePago.current = null; }
        return;
      }
      let antes = null;
      try { antes = JSON.parse(sessionStorage.getItem(CLAVE_RECARGA) || "null"); } catch { antes = null; }
      vueltaDePago.current = null;
      if (!antes || antes.cid !== cid) { sessionStorage.removeItem(CLAVE_RECARGA); return; }
      let n = 0;
      const revisar = (d) => {
        if (!vivo || !d) return false;
        if ((d.saldo_paquetes || 0) > (antes.saldo || 0)) {
          // El tamaño del paquete comprado; si no se guardó, lo que de verdad subió el saldo.
          setRecargaConv(antes.conversaciones ?? (d.saldo_paquetes - (antes.saldo || 0)));
          setRecarga("acreditada");
          sessionStorage.removeItem(CLAVE_RECARGA);
          return true;
        }
        if ((d.pagos_pendientes || []).length > 0) { sessionStorage.removeItem(CLAVE_RECARGA); return true; }
        return false;
      };
      if (revisar(data)) return;
      const sondear = async () => {
        n += 1;
        const d = await cargar();
        if (revisar(d) || n >= SONDEOS_TRAS_PAGO) { sessionStorage.removeItem(CLAVE_RECARGA); return; }
        timer = setTimeout(sondear, INTERVALO_SONDEO_MS);
      };
      timer = setTimeout(sondear, INTERVALO_SONDEO_MS);
    })();
    return () => { vivo = false; if (timer) clearTimeout(timer); };
  }, [cid, debePreguntar, cargar]);

  // POST -> redirige a Stripe. Devuelve false si falló (la UI sólo vuelve a habilitar el botón).
  const iniciarRecarga = useCallback(async () => {
    try {
      const { data } = await api.post("/paquetes/checkout");
      const url = data?.checkout_url;
      if (typeof url !== "string" || !url.startsWith("https://")) return false;
      sessionStorage.setItem(CLAVE_RECARGA, JSON.stringify({
        cid,
        saldo: consumo?.saldo_paquetes || 0,
        conversaciones: consumo?.paquete?.conversaciones ?? null,
      }));
      window.location.assign(url);
      return true;
    } catch {
      return false;
    }
  }, [cid, consumo]);

  return (
    <ConsumoCtx.Provider value={{ consumo, recarga, recargaConv, iniciarRecarga, refrescarConsumo: cargar }}>
      {children}
    </ConsumoCtx.Provider>
  );
}

export function useConsumo() {
  return useContext(ConsumoCtx);
}
